/**
 * CORE-1350 (П3) — отказ отправки перестаёт съедать написанное.
 *
 * Из отчёта: «Несколько раз вводишь фразу, отправляя её в чате с ИИ, но она не
 * публикуется, а просто исчезает. Снова работает после перезагрузки страницы.»
 *
 * ─── Что происходило ───
 *
 * Композер чистит поле сразу и безусловно (`AgapellaChatThread.submit`), потому
 * что `onSend` объявлен возвращающим `void` — отказу физически некуда прийти.
 * Ниже по течению `useChatThreadSource.send` ловил отклонение и записывал
 * `chat_send_failed` в телеметрию. И всё: ни сообщения в ленте, ни текста в
 * поле, ни строчки человеку.
 *
 * Отправка при этом разрешена на мёртвом сокете — `canSend` собран из длины
 * текста и вложений и о состоянии соединения не знает вовсе. Тогда
 * `ChatApiChannel.call` спотыкается о `requireConn()` и отклоняет вызов, ни разу
 * не сходив на сервер. Отсюда и «помогает перезагрузка»: она поднимает
 * соединение заново.
 *
 * ─── Почему одного «повторить» мало ───
 *
 * Отказы делятся надвое, и обходятся с ними по-разному.
 *
 * Сорванный по времени вызов (`DeadlineExceededError`, 12 с у `SendMessage`) —
 * это НЕ «не отправилось». Сервер, скорее всего, сообщение принял, и после
 * перезапуска канала оно приедет вместе с историей. Такой исход здесь
 * называется `unconfirmed`: человеку говорим, что подтверждения нет.
 *
 * CORE-1791 (возврат QA 28.10) — прежде на `unconfirmed` текст в поле не
 * возвращали: повтор дал бы дубль. Теперь каждая отправка несёт ключ повтора
 * (`clientMessageId`), повтор того же текста идёт с тем же ключом, и сервер
 * второй копии не создаёт. Поэтому текст возвращается при любом отказе, а
 * дошедшее сообщение убирает его из поля само (`AgapellaChatThread`).
 *
 * Всё остальное — отказ по существу: либо вызов не ушёл (сокет не подключён),
 * либо хаб ответил «нельзя» (`moderation_blocked`, `muted`, `blocked`,
 * `forbidden`, пустое сообщение). Здесь сервер сообщения не сохранил, оно не
 * вернётся ниоткуда, и написанное обязано вернуться в композер.
 *
 * ─── Про строки ───
 *
 * SignalR передаёт текст `HubException` клиенту дословно (это же свойство
 * держит `hubRefusal.ts` и `chatChannelError.ts`), поэтому здесь сравнение по
 * строке — не хрупкость, а контракт. Токены взяты у `ChatHub.SendMessage` и
 * `ChatService.CanPostAsync`; незнакомый отказ получает спокойную общую
 * формулировку, а не машинный токен в лицо.
 */
import { DeadlineExceededError } from './withDeadline';

export type SendOutcome =
  /** Хаб принял сообщение. */
  | 'sent'
  /** Сервер сообщения не сохранил — текст надо вернуть человеку. */
  | 'rejected'
  /** Ответа не дождались; сообщение могло дойти. Текст возвращается, повтор — с тем же ключом. */
  | 'unconfirmed';

export interface SendFailure {
  outcome: Exclude<SendOutcome, 'sent'>;
  /** Что показать человеку. Готовая строка, брендовый спокойный тон. */
  message: string;
  /** Машинная причина — для `chat_send_failed`, не для показа. */
  reason: string;
}

/**
 * Чем кончилась отправка. Живёт здесь, а не в хуке: тип нужен и композеру, а
 * компоненту не следует тянуть импорт из слоя хуков ради одного объявления.
 */
export type SendResult = { outcome: 'sent' } | SendFailure;

/** Отказ не отправлять: сокета нет. Бросает `ChatApiChannel.requireConn`. */
const NOT_CONNECTED = 'ChatApi hub is not connected';

/**
 * Токены отказа от `ChatHub.SendMessage` и `CanPostAsync`.
 *
 * Пустое сообщение и битый JSON вложений сюда включены не ради человека — до
 * них композер не пускает, — а чтобы незнакомый токен нельзя было спутать со
 * знакомым: список закрытый, всё вне его идёт в общую формулировку.
 */
const REFUSALS: Record<string, string> = {
  moderation_blocked: 'Это сообщение не проходит проверку правил. Попробуйте сказать иначе.',
  muted: 'Сейчас вы не можете писать в этот чат.',
  blocked: 'Отправка в этот чат недоступна.',
  forbidden: 'Писать в этот чат нельзя.',
  'channel not found': 'Чат не найден. Обновите страницу.',
  'text or attachments required': 'Сообщение пустое.',
  'attachmentsJson must be valid JSON': 'Не удалось приложить файлы. Попробуйте ещё раз.',
  // CORE-1682 — `SendMessageV2` с `attachmentIds`.
  too_many_attachments: 'Слишком много файлов в одном сообщении.',
  attachment_invalid: 'Один из файлов больше недоступен. Прикрепите его заново.',
  attachments_conflict: 'Не удалось приложить файлы. Попробуйте ещё раз.',
  client_message_id_invalid: 'Не удалось отправить. Попробуйте ещё раз.',
  // CORE-1685 — реакции только из комплекта.
  unknown_reaction: 'Такой реакции больше нет. Выберите из набора.',
  // CORE-1698 — мастер закрыл обсуждение, пока сообщение набиралось: сервер
  // проверяет актуальное состояние и не делает вид, что принял.
  discussion_closed: 'Обсуждение закрыто, сообщение не отправлено. Текст сохранён.',
};

function messageOf(err: unknown): string {
  if (typeof err === 'string') return err.trim();
  if (err instanceof Error) return err.message.trim();
  return '';
}

/**
 * Разобрать отклонение `send` в исход и строку для человека.
 *
 * Вызывается только на отказе — успех сюда не приходит.
 */
/**
 * Знает ли устройство, что оно офлайн.
 *
 * `navigator.onLine` есть на вебе; на нативе его нет, и «не знаю» здесь —
 * честный ответ, а не `false`. Разница существенная: на «не знаю» правило ниже
 * обязано оставить прежнее поведение, а не объявить отправку несостоявшейся.
 */
function deviceIsOffline(): boolean | null {
  const nav = (globalThis as { navigator?: { onLine?: unknown } }).navigator;
  if (!nav || typeof nav.onLine !== 'boolean') return null;
  return nav.onLine === false;
}

export function classifySendFailure(err: unknown): SendFailure {
  if (err instanceof DeadlineExceededError) {
    // CORE-1350 (возврат QA 22.09) — «отключил сеть, написал сообщение,
    // отправил: сообщение не ушло, но из поля исчезло полностью».
    //
    // Довод про «сервер, скорее всего, принял» верен ровно до тех пор, пока
    // связь есть. Если устройство офлайн, сервер не принял ничего, и
    // осторожность превращается в потерю написанного. Офлайн — это не
    // «неизвестно», это определённый отказ.
    if (deviceIsOffline() === true) {
      return {
        outcome: 'rejected',
        message: 'Связи нет — сообщение не отправлено. Текст сохранён, попробуйте ещё раз.',
        reason: 'offline_deadline',
      };
    }
    return {
      outcome: 'unconfirmed',
      // Ни «отправлено», ни «не отправлено» сказать честно нельзя, поэтому и не
      // говорим ни того, ни другого. Обещание про «появится само» настоящее:
      // канал перезапускается и вытягивает историю.
      message: 'Ответ от чата не пришёл вовремя. Текст сохранён: если сообщение не появится, отправьте ещё раз.',
      reason: 'deadline',
    };
  }

  const raw = messageOf(err);

  if (raw === NOT_CONNECTED) {
    return {
      outcome: 'rejected',
      message: 'Связь с чатом потеряна — сообщение не отправлено. Текст сохранён, попробуйте ещё раз.',
      reason: 'not_connected',
    };
  }

  const known = REFUSALS[raw];
  if (known) return { outcome: 'rejected', message: known, reason: raw };

  return {
    outcome: 'rejected',
    message: 'Сообщение не отправилось. Текст сохранён, попробуйте ещё раз.',
    reason: raw || 'unknown',
  };
}
